// AUTH_SERVICE
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { Logger, LoggingService } from 'ionic-logging-service';
import { Observable, of, Subscriber, throwError } from 'rxjs';
import { catchError, first, map, retry, switchMap } from 'rxjs/operators';
import { EnvironmentService } from '../environment/environment.service';
import { PlatformService } from '../platform/platform.service';
import { StorageService } from '../storage/storage.service';
import { ITokens } from './models/tokens.model';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private isCordova: boolean = this.platformService.isCordova();
  public isAuthenticatedSignal = signal(false);
  private logger: Logger;
  public  AuthAccessToken: string = 'AuthAccessToken';
  public  AuthRefreshToken: string = 'AuthRefreshToken';
  public  TokenTimeStamp: string = 'TokenTimeStamp';
  private TokenExpiresIn: string = 'AuthTokenTimeout'; // key-variable named after server naming(expires_in) for better readability. 'AuthTokenTimeout' string is used for persitence between ionic-release-app(this) and admin-app
  private authTokenKeys: string[] = [this.AuthAccessToken, this.AuthRefreshToken, this.TokenExpiresIn, this.TokenTimeStamp];


  private appLink: string;
  private endpointCodeExchange: string;
  private authorizationQueryParams: any;
  private endpointLogOut: string;

  constructor(
    private environmentService: EnvironmentService,
    private httpClient: HttpClient,
    private loggingService: LoggingService,
    private platformService: PlatformService,
    private storageService: StorageService
  ) {
    this.logger = this.loggingService.getLogger("[AuthService]");
    const methodName = "ctor";
    this.logger.entry(methodName);
  }

  public removeStorageData(): Observable<void> {
    this.logger.info('removeStorageData()');
    return new Observable<void>((subscriber: Subscriber<void>) => {
      this.storageService.removeStoredItems(this.authTokenKeys).subscribe(() => {
        this.isAuthenticatedSignal.set(false);
        subscriber.next();
        subscriber.complete();
      });
    });
  }

  public setStorageData(tokens: ITokens): Observable<void> {
    this.logger.info('setStoreageData()');
    const access_token = tokens.access_token;
    const refresh_token = tokens.refresh_token;
    const expires_in = tokens.expires_in;
    return new Observable<void>((subscriber: Subscriber<void>) => {
      this.storageService.addItemToLocalStorage(this.AuthRefreshToken, refresh_token, 'setStorageData()').subscribe(() => {
        this.isAuthenticatedSignal.set(true);
        this.storageService.addItemToLocalStorage(this.AuthAccessToken, access_token, 'setStorageData()').subscribe(() => {
          this.storageService.addItemToLocalStorage(this.TokenTimeStamp, Date.now(), 'setStorageData()').subscribe(() => {
            this.storageService.addItemToLocalStorage(this.TokenExpiresIn, expires_in, 'setStorageData()').subscribe(() => {
              subscriber.next();
              subscriber.complete();
            });
          });
        });
      });
    });
  }

  public verifyUserCredentials(): Observable<void> {
    this.logger.info('verifyUserCredentials() get refreshToken from storage');
    return new Observable<void>((subscriber: Subscriber<void>) => {
      this.storageService.getItemFromLocalStorage(this.AuthRefreshToken, 'verifyUserCredentials()').subscribe((refreshToken: string) => {
        if (refreshToken) {
          this.isAuthenticatedSignal.set(true);
          subscriber.next();
          subscriber.complete();
        } else {
          this.logger.info('verifyUserCredentials(): No stored refreshToken');
          subscriber.error();
          subscriber.complete();
        }
      });
    });
  }

  public authenticateSignedInUser(signInCode: string, tenantDomain: string): Observable<void> {
    this.logger.info('authenticateSignedInUser()');

    this.endpointCodeExchange = this.environmentService.getEnvironmentEndpoint['endpointCodeExchange'];
    this.authorizationQueryParams = this.environmentService.getEnvironmentEndpoint['authorizationQueryParams'];
    this.appLink = this.environmentService.getEnvironmentEndpoint['appLink'];


    const redirectUri = new URL(this.isCordova
      ? `${this.appLink}?${tenantDomain}`
      : location.origin);

    // URLSearchParams for POST body
    const authParams = new URLSearchParams();
    this.authorizationQueryParams.forEach(param => {
      const key = Object.keys(param)[0];
      let value = param[key];
      if (key === 'redirect_uri') {value = redirectUri.toString();}
      if (key === 'code') {value = signInCode;}
      authParams.set(key, value);
    });

    this.logger.info('## AuthService authenticateSignedInUser() authParams: ' + authParams.toString());
    const dataToSend = authParams.toString();

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    return this.httpClient.post<ITokens>(this.endpointCodeExchange, dataToSend, { headers }).pipe(
      retry(1),
      switchMap((tokens: ITokens) => this.setStorageData(tokens)),
      catchError((error: HttpErrorResponse) => {
        this.logger.error('authenticateSignedInUser() error:', error);
        return throwError(() => error);
      })
    );
  }

  public getAccessToken(url?): Observable<string> {
    this.logger.info('getAccessToken()');
    return this.checkIfAccessTokenIsValid(url).pipe(
      switchMap((isTokenUsable: boolean) => {
        if (isTokenUsable) {
          return this.storageService.getItemFromLocalStorage(this.AuthAccessToken, 'getAccessToken()').pipe(
            switchMap((accessToken: string) => {
              if (accessToken) {
                return of(accessToken);
              } else {
                this.logger.info('Missing accessToken - refreshing token');
                return this.refreshAccessToken();
              }
            })
          );
        } else {
          return this.refreshAccessToken();
        }
      }),
      catchError((error) => {
        this.logger.error('getAccessToken() error:', error);
        return throwError(() => error);
      })
    );
  }

  private checkIfAccessTokenIsValid(url): Observable<boolean> {
    this.logger.info('checkIfAccessTokenIsValid() for url: ' + url);

    const now = Date.now();

    return this.storageService.getItemFromLocalStorage(this.TokenExpiresIn, 'checkIfAccessTokenIsValid').pipe(
      switchMap((tokenExpiresIn: number) => {
        if (!tokenExpiresIn) {
          return of(false);
        }
        return this.storageService.getItemFromLocalStorage(this.TokenTimeStamp, 'checkIfAccessTokenIsValid').pipe(
          map((tokenTimeStamp: number) => {
            if (!tokenTimeStamp) {
              return false;
            }
            const tokenTimeOutAdjusted = tokenExpiresIn - 20;
            const isTokenValid = (tokenTimeStamp + tokenTimeOutAdjusted * 1000) > now;
            return isTokenValid;
          })
        );
      })
    );
  }

  public refreshAccessToken(tokenRefresh?: string): Observable<string> {
    this.logger.info('refreshAccessToken()');
    const getToken$ = tokenRefresh
      ? of(tokenRefresh)
      : this.storageService.getItemFromLocalStorage(this.AuthRefreshToken, 'refreshAccessToken()');

    return getToken$.pipe(
      switchMap((refreshToken: string) => {
        if (!refreshToken) {
          return throwError(() => new Error('No refresh token'));
        }
        this.endpointCodeExchange = this.environmentService.getEnvironmentEndpoint['endpointCodeExchange'];
        const headers = new HttpHeaders({
          'Content-Type': 'application/x-www-form-urlencoded'
        });
        const dataToSend = `grant_type=refresh_token&client_id=web_app&client_secret=thehouseisonfire&refresh_token=${refreshToken}`;
        return this.httpClient.post<ITokens>(this.endpointCodeExchange, dataToSend, { headers }).pipe(
          retry(1),
          first(),
          switchMap((tokens: ITokens) => {
            this.isAuthenticatedSignal.set(true);
            return this.setStorageData(tokens).pipe(
              switchMap(() =>
                this.storageService.getItemFromLocalStorage(this.AuthAccessToken, 'refreshAccessToken()')
              )
            );
          }),
          catchError((err) => {
            this.logger.error('refreshAccessToken() failed:', err);

            // If refresh token is invalid (401), clear storage and mark as unauthenticated
            if (err.status === 401) {
              this.logger.info('Refresh token invalid - clearing user session');
              this.isAuthenticatedSignal.set(false);
              // Clear stored tokens asynchronously to avoid blocking
              this.removeStorageData().subscribe({
                next: () => this.logger.info('Storage cleared after invalid refresh token'),
                error: (clearError) => this.logger.error('Error clearing storage:', clearError)
              });
            }

            return throwError(() => err);
          })
        );
      }),
      catchError((err) => {
        this.logger.error('No refresh token or error in refreshAccessToken:', err);
        return throwError(() => err);
      })
    );
  }


  public deAuthenticateUser(): Observable<void> {
    this.logger.info('deAuthenticateUser()');

   return this.storageService.getItemFromLocalStorage(this.AuthRefreshToken, 'deAuthenticateUser()').pipe(
      switchMap((tokenRefresh: string) => {
        if (tokenRefresh) {
          this.endpointLogOut = this.environmentService.getEnvironmentEndpoint['endpointLogOut'];
          const headers = new HttpHeaders({
            'Content-Type': 'application/x-www-form-urlencoded'
          });
          return this.httpClient.get<void>(this.endpointLogOut + tokenRefresh, { headers }).pipe(
            switchMap(() => this.removeStorageData()),
            catchError((error: HttpErrorResponse) => {
              this.logger.error('deAuthenticateUser() http error:', error);
              return this.removeStorageData().pipe(
                switchMap(() => throwError(() => error))
              );
            })
          );
        } else {
          return this.removeStorageData();
        }
      }),
      catchError((error) => {
        this.logger.error('deAuthenticateUser() error:', error);
        return throwError(() => error);
      })
    );
  }
}